import { Injectable } from '@angular/core';
import { Apollo } from 'apollo-angular';
import { BehaviorSubject, catchError, map, Observable, of, switchMap, tap } from 'rxjs';
import { Shipping } from './shipping';
import { ShippingListParams } from './shipping.types';
import { destinationPortsFromFreights, FreightDestinationPort, freightQuery } from '@core/proposals/freight-options';

import {
  ADD_SHIPPING,
  GET_ALL_SHIPPING,
  GET_FREIGHT_DESTINATION_PORTS,
  GET_SHIPPING_BY_ID,
  UP_SHIPPING,
} from '@queries/shipping.graphql';

@Injectable({
  providedIn: 'root',
})
export class ShippingService {
  private _item$: BehaviorSubject<Shipping | null> = new BehaviorSubject(null);
  private _items$: BehaviorSubject<Shipping[] | null> = new BehaviorSubject(
    null,
  );

  private _item: Shipping | null;
  private _items: Shipping[] | null;

  paginationTotal = 0;

  /** Params of the last list page loaded; cleared when a save makes it stale. */
  private _lastListKey: string | null = null;

  constructor(private apollo: Apollo) { }

  // -----------------------------------------------------------------------------------------------------
  // @ Accessors
  // -----------------------------------------------------------------------------------------------------

  get shipping$(): Observable<Shipping> {
    return this._item$.asObservable();
  }

  get shippings$(): Observable<Shipping[]> {
    return this._items$.asObservable();
  }

  // -----------------------------------------------------------------------------------------------------
  // @ Public methods
  // -----------------------------------------------------------------------------------------------------

  /**
   * The rows already loaded for exactly these params (e.g. by the route
   * resolver), or null when they were never loaded or a save made them stale.
   */
  cachedList(params: ShippingListParams): Shipping[] | null {
    return this._lastListKey !== null && this._lastListKey === JSON.stringify(params)
      ? this._items
      : null;
  }

  /** Destination ports with freights from originPortId (and container type), for the proposal freight dialogs. */
  getDestinationPorts(originPortId: number, quantityTypeId?: number): Observable<FreightDestinationPort[]> {
    return this.apollo
      // eslint-disable-next-line @typescript-eslint/naming-convention
      .query<{ FreightDestinationPorts: FreightDestinationPort[] }>({
        query: GET_FREIGHT_DESTINATION_PORTS,
        variables: { originPortId, quantityTypeId },
        fetchPolicy: 'network-only',
      })
      .pipe(
        map(({ data }) => data.FreightDestinationPorts ?? []),
        // An API deployed before FreightDestinationPorts rejects the query: take the ports of the freights instead.
        catchError(() => this.destinationPortsOfFreights(originPortId, quantityTypeId)),
      );
  }

  /** Ports of the first FreightFindAll page, without touching the freight list state of this service. */
  private destinationPortsOfFreights(originPortId: number, quantityTypeId?: number): Observable<FreightDestinationPort[]> {
    return this.apollo
      // eslint-disable-next-line @typescript-eslint/naming-convention
      .query<{ FreightFindAll: { data: Shipping[] } }>({
        query: GET_ALL_SHIPPING,
        variables: freightQuery({ originPortId, quantityTypeId }),
        fetchPolicy: 'network-only',
      })
      .pipe(map(({ data }) => destinationPortsFromFreights(data.FreightFindAll?.data as any)));
  }

  getList(params: ShippingListParams): Observable<Shipping[]> {
    const key = JSON.stringify(params);
    return (
      this.apollo
        // eslint-disable-next-line @typescript-eslint/naming-convention
        .watchQuery<{ FreightFindAll: { data: Shipping[]; total: number } }>({
          query: GET_ALL_SHIPPING,
          variables: {
            where: params.where,
            orderBy: params.orderBy,
            skip: params.skip,
            take: params.take,
          },
          fetchPolicy: 'network-only',
        })
        .valueChanges.pipe(
          switchMap(({ data }) => {
            const freightTypes = data.FreightFindAll.data;
            this.paginationTotal = data.FreightFindAll.total;
            this._items = freightTypes;
            this._lastListKey = key;
            this._items$.next(this._items);
            return of(this._items);
          }),
        )
    );
  }

  getById(id: number): Observable<Shipping> {
    if (!id) {
      this._item = [];
      this._item$.next(this._item);
      return of(this._item);
    }
    return this.apollo
      .watchQuery<any>({
        query: GET_SHIPPING_BY_ID,
        variables: {
          where: {
            id,
          },
        },
        fetchPolicy: 'network-only',
      })
      .valueChanges.pipe(
        switchMap(({ data }) => {
          const department = data.FreightFindOne;
          this._item = department;
          this._item$.next(this._item);
          return of(this._item);
        }),
      );
  }

  create(record: Shipping) {
    return this.apollo
      .mutate<any>({
        mutation: ADD_SHIPPING,
        variables: {
          data: {
            requestDate: record.requestDate,
            ql: record.ql,
            validity: record.validity,
            continent: record.continent,
            via: record.via,
            tt: +record.tt,
            originPort: { connect: { id: +record.originPortId } },
            originCountry: { connect: { id: +record.originCountryId } },

            destinyPort: { connect: { id: +record.destinyPortId } },
            destinyCountry: { connect: { id: +record.destinyCountryId } },

            freightType: { connect: { id: +record.freightTypeId } },
            company: { connect: { id: +record.companyId } },

            // eslint-disable-next-line @typescript-eslint/naming-convention
            Pricing: {
              createMany: {
                data: [
                  {
                    value: +record.Pricing.value,
                    currencyId: +record.Pricing.currencyId,
                    isMainCurrency: record.Pricing.isMainCurrency,
                  },
                ],
                skipDuplicates: true,
              },
            },
          },
        },
      })
      .pipe(
        map(({ data }) => data?.FreightCreate),
        tap(() => (this._lastListKey = null)),
      );
  }

  update(record: Shipping) {
    return this.apollo
      .mutate<any>({
        mutation: UP_SHIPPING,
        variables: {
          data: {
            requestDate: { set: record.requestDate },
            ql: { set: record.ql },
            validity: { set: record.validity },
            continent: { set: record.continent },
            via: { set: record.via },
            tt: { set: +record.tt },
            originPort: { connect: { id: +record.originPortId } },
            originCountry: { connect: { id: +record.originCountryId } },

            destinyPort: { connect: { id: +record.destinyPortId } },
            destinyCountry: { connect: { id: +record.destinyCountryId } },

            freightType: { connect: { id: +record.freightTypeId } },
            company: { connect: { id: +record.companyId } },

            // eslint-disable-next-line @typescript-eslint/naming-convention
            Pricing: {
              update: {
                where: {
                  id: +record.Pricing.id,
                },
                data: {
                  value: {
                    set: +record.Pricing.value,
                  },
                  currency: {
                    connect: {
                      id: +record.Pricing.currencyId,
                    },
                  },
                },
              },
            },
          },
          where: {
            id: +record.id,
          },
        },
      })
      .pipe(
        map(({ data }) => data?.FreightUpdate),
        tap(() => (this._lastListKey = null)),
      );
  }
}
